/*
 * Inline video embeds.
 *
 * Authors embed a video with a token, the same shape as an article reference:
 *
 *     [[video:"https://www.youtube.com/watch?v=dQw4w9WgXcQ"]]
 *     [[video:"https://youtu.be/dQw4w9WgXcQ?t=90"]]
 *     [[video:"https://vimeo.com/76979871"]]
 *     [[video:"https://cdn.example.com/clips/overview.mp4"]]
 *
 * The quotes are the documented form; a bare URI is accepted too, because a
 * hand-written token missing its quotes should embed rather than lecture.
 *
 * THE SECURITY SHAPE, which is the whole reason this file exists
 * -------------------------------------------------------------
 * "Embed whatever URI the author wrote" would mean framing arbitrary
 * third-party content inside the application's own page, and a
 * Content-Security-Policy that permits it (frame-src *) would give up exactly
 * the protection the CSP is there to provide. So this module does not embed
 * URIs. It RECOGNISES videos and CONSTRUCTS the embed URL itself:
 *
 *   - the host must be a known provider, matched against a fixed list
 *   - the video id is extracted and must match that provider's id grammar
 *   - the src handed to the iframe is built from the id, never from the input
 *
 * The consequence worth stating plainly: a URI this module does not recognise
 * is NOT embedded. It degrades to an ordinary link, so the reader can still
 * watch the video and the author can see that it did not embed. That is the
 * honest failure, and it is preferable to a silent hole in the frame policy.
 *
 * A direct media file (.mp4 and friends) is the one case where the author's URL
 * is used as given - but it goes into a <video> element, not a frame, which is
 * the same risk profile as the <img> tags the CSP already permits over https.
 *
 * Everything here is pure string handling so the browser, the server and the
 * tests share one definition of what a video token means.
 */
/** The canonical prefix, exported so no caller re-spells it. */
export const VIDEO_TOKEN_PREFIX = '[[video:';
/**
 * Where the caret goes after inserting an empty token: between the quotes, so
 * the author can paste the URI immediately. Derived from the prefix rather than
 * written as a 9 at the call site, the same way the image toolbar takes its
 * offset from the markdown its own builder produces.
 */
export const VIDEO_URI_CARET_OFFSET = `${VIDEO_TOKEN_PREFIX}"`.length;
/**
 * The token an editor toolbar inserts, wrapping a URI that has already been
 * validated by the caller.
 *
 * Validation is the caller's job on purpose: the toolbar refuses a URI the
 * allowlist does not recognise and says why, which is a better moment to find
 * out than the preview. This function's job is only to spell the token, and to
 * make sure the URI it is handed cannot break out of it.
 */
export function buildVideoToken(uri = '') {
    // A quote, a bracket or a newline would end the token early. A URI carrying
    // one is already broken, so strip rather than escape: the token grammar has
    // no escape sequence to escape into.
    const safe = uri.trim().replace(/["'[\]\r\n]/g, '');
    return `${VIDEO_TOKEN_PREFIX}"${safe}"]]`;
}
/**
 * Matches `[[video:` + anything up to `]]`, on one line.
 *
 * Deliberately loose about the inside: quoting, whitespace and the URI's own
 * grammar are sorted out by `parseVideoReference`, which has to be strict
 * anyway. Excluding `]` and newlines keeps the pattern bounded - an unclosed
 * token cannot run away over the rest of the document.
 */
const VIDEO_TOKEN_PATTERN = /\[\[video:([^\]\r\n]*?)\]\]/g;
/**
 * The frame origins the embeds actually use. The CSP's frame-src is built from
 * this list, so a provider cannot be added to the parser and then silently be
 * blocked by the policy - or permitted by the policy without the parser
 * agreeing that it is a video host.
 */
export const VIDEO_EMBED_FRAME_ORIGINS = [
    // youtube-nocookie is YouTube's privacy-preserving player: same playback, no
    // tracking cookies until the reader actually presses play.
    'https://www.youtube-nocookie.com',
    'https://player.vimeo.com',
];
const YOUTUBE_ID = /^[A-Za-z0-9_-]{6,20}$/;
const VIMEO_ID = /^[0-9]{6,12}$/;
const MEDIA_FILE = /\.(mp4|m4v|webm|ogv|ogg)$/i;
const YOUTUBE_HOSTS = new Set(['youtube.com', 'youtube-nocookie.com', 'm.youtube.com', 'youtu.be']);
/** Whole seconds from `t`/`start`; anything fancier is ignored rather than guessed. */
function startSeconds(url) {
    const raw = url.searchParams.get('t') ?? url.searchParams.get('start');
    if (!raw)
        return null;
    if (/^\d+$/.test(raw))
        return Number(raw);
    // The 1m30s / 2h5m form YouTube also produces.
    const parts = /^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?$/.exec(raw);
    if (!parts || (!parts[1] && !parts[2] && !parts[3]))
        return null;
    return Number(parts[1] ?? 0) * 3600 + Number(parts[2] ?? 0) * 60 + Number(parts[3] ?? 0);
}
function youtubeId(url) {
    const host = url.hostname.toLowerCase().replace(/^www\./, '');
    const segments = url.pathname.split('/').filter(Boolean);
    if (host === 'youtu.be')
        return segments[0] ?? null;
    if (url.pathname === '/watch')
        return url.searchParams.get('v');
    // /embed/ID, /shorts/ID, /live/ID and /v/ID all carry the id second.
    if (['embed', 'shorts', 'live', 'v'].includes(segments[0] ?? ''))
        return segments[1] ?? null;
    return null;
}
function vimeoId(url) {
    const segments = url.pathname.split('/').filter(Boolean);
    if (url.hostname.toLowerCase().replace(/^www\./, '') === 'player.vimeo.com') {
        return segments[0] === 'video' ? (segments[1] ?? null) : null;
    }
    // vimeo.com/123456 and vimeo.com/channels/staffpicks/123456 both end in the id.
    const last = segments[segments.length - 1];
    return last && VIMEO_ID.test(last) ? last : null;
}
/**
 * Recognises an embeddable video, or returns null.
 *
 * Called wherever an embed is built - including a second time in the browser,
 * on the URI carried by the placeholder element, so that raw HTML written into
 * an article body cannot conjure an iframe to somewhere unvetted.
 */
export function parseVideoReference(raw) {
    const trimmed = raw.trim().replace(/^["']|["']$/g, '').trim();
    if (!trimmed)
        return null;
    let url;
    try {
        url = new URL(trimmed);
    }
    catch {
        return null;
    }
    // https only: http would be blocked as mixed content anyway, and every other
    // scheme (javascript:, data:, file:) has no business here.
    if (url.protocol !== 'https:')
        return null;
    const host = url.hostname.toLowerCase().replace(/^www\./, '');
    if (YOUTUBE_HOSTS.has(host)) {
        const id = youtubeId(url);
        if (!id || !YOUTUBE_ID.test(id))
            return null;
        const start = startSeconds(url);
        // Built from the validated id. The author's URL contributes nothing but
        // the id and an optional start offset.
        const src = `${VIDEO_EMBED_FRAME_ORIGINS[0]}/embed/${id}${start ? `?start=${start}` : ''}`;
        return { kind: 'frame', provider: 'youtube', src, title: 'YouTube video player' };
    }
    if (host === 'vimeo.com' || host === 'player.vimeo.com') {
        const id = vimeoId(url);
        if (!id || !VIMEO_ID.test(id))
            return null;
        return {
            kind: 'frame',
            provider: 'vimeo',
            src: `${VIDEO_EMBED_FRAME_ORIGINS[1]}/video/${id}`,
            title: 'Vimeo video player',
        };
    }
    if (MEDIA_FILE.test(url.pathname)) {
        // A media file plays in a <video> element - no frame, no third-party
        // script, the same shape as an <img> from an arbitrary https host.
        return { kind: 'file', provider: 'file', src: url.toString(), title: 'Video' };
    }
    return null;
}
/** Every video token in document order, each with its resolution attempt. */
export function parseVideoTokens(markdown) {
    const tokens = [];
    if (!markdown)
        return tokens;
    VIDEO_TOKEN_PATTERN.lastIndex = 0;
    let match;
    while ((match = VIDEO_TOKEN_PATTERN.exec(markdown)) !== null) {
        const uri = (match[1] ?? '').trim().replace(/^["']|["']$/g, '').trim();
        tokens.push({
            token: match[0],
            index: match.index,
            uri,
            embed: parseVideoReference(uri),
        });
    }
    return tokens;
}
function escapeHtmlAttribute(value) {
    return value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}
/**
 * Replaces video tokens ahead of the markdown parser.
 *
 * An embeddable video becomes an empty placeholder carrying the ORIGINAL uri;
 * the browser turns it into a real iframe or <video> after sanitising, which is
 * how the embed survives a sanitiser that (rightly) forbids author-written
 * iframes outright. A URI that is not recognised becomes a plain markdown link
 * so it is still watchable and still visibly not an embed.
 */
export function renderArticleVideos(markdown) {
    if (!markdown)
        return markdown;
    return markdown.replace(VIDEO_TOKEN_PATTERN, (_whole, inner) => {
        const uri = (inner ?? '').trim().replace(/^["']|["']$/g, '').trim();
        if (!uri)
            return '';
        const embed = parseVideoReference(uri);
        if (!embed) {
            // Markdown, not HTML: this runs before the parser, and a link is what a
            // reader can act on. Parentheses would end the link target early.
            const safe = uri.replace(/[()\s]/g, encodeURIComponent);
            return `[${uri}](${safe})`;
        }
        return `<div class="video-embed" data-video-uri="${escapeHtmlAttribute(uri)}"></div>`;
    });
}
//# sourceMappingURL=articleVideos.js.map